import { useMemo } from 'react'
import {
  GRID_COLUMNS,
  PERMISSION_GRID,
  PERMISSIONS,
  ROLE_DEFS,
  permissionsForRole,
  presetForPermissions,
  setsEqual,
} from '../lib/rbac.js'

// Role & Permissions editor: preset cards that auto-fill a modules × actions
// checkbox grid, with per-cell overrides. Any manual toggle turns the selection
// into "Custom"; a hand-built set that lands exactly on a preset snaps back to
// that preset so the stored data stays clean (role only, no override list).
//
// value:    { role, permissions } — permissions is an array of keys (custom)
//           or null/undefined (pure preset).
// onChange: (next) => void with the same shape.
// presets:  role keys the actor may assign (from rolesAssignableBy).
// grantable: Set of keys the actor themselves holds — anything outside it is
//           locked so nobody can hand out access they don't have.

function CheckIcon() {
  return (
    // `block` kills the inline-SVG baseline gap — as an inline element the tick
    // sat on the text baseline and rode high inside the box no matter what the
    // parent centred.
    <svg viewBox="0 0 24 24" fill="none" className="block h-[13px] w-[13px] shrink-0" aria-hidden="true">
      {/* coolicons: line/Check.svg */}
      <path d="M6 12L10.2426 16.2426L18.727 7.75732" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function Checkbox({ checked, disabled, onToggle, label }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
      title={disabled ? "You can't grant access you don't have" : label}
      // BRAND BLUE, not green. Green reads as a status ("passing") and this is a
      // selection control — everywhere else in the app a chosen control is
      // `primary`. inline-flex + leading-none centres the tick on both axes;
      // `align-middle` was aligning the whole box to a text baseline instead.
      className={`inline-flex h-[22px] w-[22px] flex-none items-center justify-center rounded-[6px] leading-none transition-colors ${
        checked
          ? 'bg-primary text-white [border:1px_solid_var(--primary)] hover:bg-primary-dark'
          : 'bg-panel text-transparent [border:1px_solid_var(--border)] hover:[border-color:var(--muted)]'
      } ${disabled ? 'cursor-not-allowed opacity-[0.45]' : ''}`}
    >
      {checked && <CheckIcon />}
    </button>
  )
}

export default function RolePermissions({ value, onChange, presets, grantable }) {
  const { role, permissions } = value
  // Unknown/stale keys are dropped exactly like the enforcement side does
  // (memberPermissions), so the editor and the resolver always agree.
  const effective = useMemo(
    () =>
      Array.isArray(permissions)
        ? new Set(permissions.filter((k) => PERMISSIONS[k]))
        : permissionsForRole(role),
    [role, permissions],
  )
  const selectedPreset = useMemo(() => {
    if (!Array.isArray(permissions)) return role
    if (setsEqual(effective, permissionsForRole(role))) return role
    const match = presetForPermissions(effective)
    return match !== 'custom' && presets.includes(match) ? match : 'custom'
  }, [role, permissions, effective, presets])

  // Snap a hand-toggled set back to a preset only when that's safe: the
  // member's own base role wins first (Owner and Admin share a grant set — a
  // raw match would silently rename an Admin to Owner), and any other match
  // must be a preset this actor may assign, else it stays Custom.
  function commit(nextSet) {
    if (setsEqual(nextSet, permissionsForRole(role))) {
      onChange({ role, permissions: null })
      return
    }
    const snap = presetForPermissions(nextSet)
    if (snap !== 'custom' && presets.includes(snap)) onChange({ role: snap, permissions: null })
    else onChange({ role, permissions: [...nextSet] })
  }

  function toggleKey(key) {
    if (!grantable.has(key)) return
    const next = new Set(effective)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    commit(next)
  }

  function toggleKeys(keys) {
    const usable = keys.filter((k) => grantable.has(k))
    if (!usable.length) return
    const next = new Set(effective)
    const allOn = usable.every((k) => next.has(k))
    usable.forEach((k) => (allOn ? next.delete(k) : next.add(k)))
    commit(next)
  }

  const presetCards = [...presets, 'custom']

  return (
    <div>
      <div className="mb-4 grid grid-cols-2 gap-3 max-[720px]:grid-cols-1">
        {presetCards.map((r) => {
          const isCustom = r === 'custom'
          const active = selectedPreset === r
          return (
            <button
              key={r}
              type="button"
              onClick={() => {
                if (isCustom) return
                // Preset cards obey the same boundary as the cells: grants the
                // actor doesn't hold are stripped, leaving a Custom subset.
                const target = permissionsForRole(r)
                const clamped = [...target].filter((k) => grantable.has(k))
                if (clamped.length === target.size) onChange({ role: r, permissions: null })
                else onChange({ role: r, permissions: clamped })
              }}
              className={`rounded-[12px] border px-4 py-3 text-left transition-colors ${
                active ? 'border-primary bg-primary-tint' : 'border-line bg-panel hover:border-faint'
              } ${isCustom && !active ? 'cursor-default opacity-70' : ''}`}
            >
              <span className={`block text-[14px] font-semibold ${active ? 'text-primary-dark' : 'text-ink'}`}>
                {isCustom ? 'Custom' : ROLE_DEFS[r].label}
              </span>
              <span className="mt-0.5 block text-[12.5px] leading-[1.45] text-dim">
                {isCustom ? 'Manually configured permissions' : ROLE_DEFS[r].blurb}
              </span>
            </button>
          )
        })}
      </div>

      <div className="overflow-x-auto rounded-[12px] border border-line bg-surface">
        <table className="w-full min-w-[640px] border-collapse text-[13px]">
          <thead>
            <tr className="[border-bottom:1px_solid_var(--border)] bg-panel">
              <th className="px-4 py-[10px] text-left font-semibold text-dim">Module</th>
              {GRID_COLUMNS.map((col) => {
                const keys = PERMISSION_GRID.flatMap((m) => (m.cells[col] ? [m.cells[col]] : []))
                const usable = keys.some((k) => grantable.has(k))
                return (
                  <th key={col} className="px-2 py-[10px] text-center font-semibold text-dim">
                    <button
                      type="button"
                      onClick={() => toggleKeys(keys)}
                      disabled={!usable}
                      title={usable ? `Toggle ${col} for every module` : ''}
                      className={`rounded-[5px] bg-transparent px-1 py-0.5 font-semibold text-dim ${
                        usable ? 'hover:text-ink' : 'cursor-default'
                      }`}
                    >
                      {col}
                    </button>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {PERMISSION_GRID.map((row) => {
              const keys = Object.values(row.cells)
              const granted = keys.filter((k) => effective.has(k)).length
              const rowUsable = keys.some((k) => grantable.has(k))
              return (
                <tr key={row.module} className="[&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]">
                  <td className="px-4 py-[11px]">
                    <div className="flex items-center gap-x-3">
                      <Checkbox
                        checked={granted === keys.length && granted > 0}
                        disabled={!rowUsable}
                        onToggle={() => toggleKeys(keys)}
                        label={`Toggle all ${row.module} permissions`}
                      />
                      <div>
                        <div className="font-semibold text-ink">{row.module}</div>
                        <div className="mt-px text-[11.5px] text-faint">
                          {granted}/{keys.length} granted
                        </div>
                      </div>
                    </div>
                  </td>
                  {GRID_COLUMNS.map((col) => {
                    const key = row.cells[col]
                    if (!key) {
                      return (
                        <td key={col} className="px-2 py-[11px] text-center text-faint">
                          —
                        </td>
                      )
                    }
                    return (
                      <td key={col} className="px-2 py-[11px] text-center">
                        <Checkbox
                          checked={effective.has(key)}
                          disabled={!grantable.has(key)}
                          onToggle={() => toggleKey(key)}
                          label={`${row.module}: ${col}`}
                        />
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
